import { computed, inject, Injectable } from '@angular/core';
import { StoreService } from './store.service';
import { CoverageLink, WorksheetRow, WorksheetSummary } from '../models';

@Injectable({
  providedIn: 'root'
})
export class BusinessLogicService {
  private readonly store = inject(StoreService);

  private roundMetric(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private matchesWorksheetFilter(row: WorksheetRow, filterString: string): boolean {
    const filter = filterString.trim().toLowerCase();
    if (!filter) {
      return true;
    }

    const searchStr = `${row.componentDesignator} ${row.componentType} ${row.failureName} ${row.failureModeName} ${row.safetyMechanism} ${row.systemEffect}`.toLowerCase();
    return searchStr.includes(filter);
  }

  readonly worksheetRows = computed(() => {
    const filter = this.store.searchQuery();
    const analysisByKey = new Map(this.store.analysis().map((row) => [`${row.component}::${row.failure}`, row]));
    const componentById = new Map(this.store.components().map((component) => [component.component_designator, component]));

    // Build SPF and LATENT coverage maps from coverage links
    const spfCoverageByFi = new Map<string, CoverageLink>();
    const latCoverageByFi = new Map<string, CoverageLink>();
    for (const link of this.store.coverageLinks()) {
      if (link.mechanism_type === 'LATENT') {
        latCoverageByFi.set(link.failure_instance_id, link);
      } else {
        spfCoverageByFi.set(link.failure_instance_id, link);
      }
    }

    const rowsFromFailures = this.store.failureInstances().map((failureInstance) => {
      const component = componentById.get(failureInstance.component_designator);
      const spfLink = spfCoverageByFi.get(failureInstance.id);
      const latLink = latCoverageByFi.get(failureInstance.id);
      const analysisRow = analysisByKey.get(`${failureInstance.component_designator}::${failureInstance.name}`);
      const componentType = this.store.componentTypes().find((type) => type.name === component?.component_type);
      const failureMode = componentType?.failure_modes.find((mode) => mode.code === failureInstance.failure_mode_code);
      const systemEffectName = failureInstance.system_effect_name ?? 'Unlinked';
      const safetyGoal = failureInstance.safety_goal_id
        ? `${failureInstance.safety_goal_id} ${failureInstance.safety_goal_description ?? ''}`.trim()
        : 'Unassigned';

      // DC values
      const dcSpf = analysisRow?.dc_spf ?? spfLink?.diagnostic_coverage ?? failureInstance.diagnostic_coverages[0] ?? 0;
      const dcLat = analysisRow?.dc_lat ?? latLink?.diagnostic_coverage ?? failureInstance.latent_diagnostic_coverages[0] ?? 0;

      // Total FIT (temperature-adjusted if analysis available)
      const totalFit = analysisRow?.total_fit ?? failureMode?.base_rate_value ?? 0;

      // λ_SPF = λ × (1 − DC_primary)
      const spfFit = analysisRow?.spf_fit ?? totalFit * (1 - dcSpf);
      // λ_LF = (λ × DC_primary) × (1 − DC_secondary)
      const latentFit = analysisRow?.latent_fit ?? (totalFit * dcSpf) * (1 - dcLat);

      const residualFit = spfFit;
      const protectedFit = Math.max(totalFit - residualFit, 0);
      const singlePointPct = totalFit > 0 ? (spfFit / totalFit) * 100 : 0;
      const latentFaultPct = totalFit > 0 ? (latentFit / totalFit) * 100 : 0;
      const riskBand: WorksheetRow['riskBand'] = residualFit >= 10 ? 'high' : residualFit >= 1 ? 'medium' : 'low';

      return {
        componentDesignator: failureInstance.component_designator,
        componentType: component?.component_type ?? 'Unknown',
        failureInstanceId: failureInstance.id,
        failureName: failureInstance.name,
        failureModeCode: failureInstance.failure_mode_code,
        failureModeName: failureInstance.failure_mode_name ?? failureMode?.name ?? failureInstance.failure_mode_code,
        totalFit: this.roundMetric(totalFit),
        protectedFit: this.roundMetric(protectedFit),
        residualFit: this.roundMetric(residualFit),
        diagnosticCoveragePct: this.roundMetric(dcSpf * 100),
        singlePointPct: this.roundMetric(singlePointPct),
        latentFaultPct: this.roundMetric(latentFaultPct),
        spfFit: this.roundMetric(spfFit),
        latentFit: this.roundMetric(latentFit),
        dcSpf: this.roundMetric(dcSpf),
        dcLat: this.roundMetric(dcLat),
        safetyMechanism: spfLink?.safety_mechanism_name ?? failureInstance.safety_mechanism_names[0] ?? 'None',
        latentMechanism: latLink?.safety_mechanism_name ?? failureInstance.latent_mechanism_names[0] ?? 'None',
        failureEffect: failureInstance.failure_effect_name ?? 'Unlinked',
        systemEffect: systemEffectName,
        safetyGoal,
        operatingTemp: component?.op_temp ?? 0,
        riskBand,
        safetyRelevant: Boolean(failureInstance.system_effect_name)
      };
    });

    const componentDesignatorsWithRows = new Set(rowsFromFailures.map((row) => row.componentDesignator));
    const incompleteRows = this.store.components()
      .filter((component) => !componentDesignatorsWithRows.has(component.component_designator))
      .map((component) => {
        return {
          componentDesignator: component.component_designator,
          componentType: component.component_type ?? 'Unassigned',
          failureInstanceId: `UNLINKED-${component.component_designator}`,
          failureName: component.component_type ? 'No failure instance linked' : 'Pending type assignment',
          failureModeCode: '-',
          failureModeName: component.component_type ? 'Incomplete' : 'No type',
          totalFit: 0,
          protectedFit: 0,
          residualFit: 0,
          diagnosticCoveragePct: 0,
          singlePointPct: 0,
          latentFaultPct: 0,
          spfFit: 0,
          latentFit: 0,
          dcSpf: 0,
          dcLat: 0,
          safetyMechanism: 'None',
          latentMechanism: 'None',
          failureEffect: 'Unlinked',
          systemEffect: 'Unlinked',
          safetyGoal: 'Unassigned',
          operatingTemp: component.op_temp ?? 0,
          riskBand: 'incomplete' as const,
          safetyRelevant: false,
        };
      });

    return [...rowsFromFailures, ...incompleteRows]
      .filter((row) => this.matchesWorksheetFilter(row, filter))
      .sort((left, right) => {
        if (left.componentDesignator === right.componentDesignator) {
          return left.failureInstanceId.localeCompare(right.failureInstanceId);
        }
        return left.componentDesignator.localeCompare(right.componentDesignator);
      });
  });

  readonly worksheetSummary = computed<WorksheetSummary>(() => {
    const rows = this.worksheetRows();
    const failureRows = rows.filter((row) => row.riskBand !== 'incomplete');
    const totalFit = failureRows.reduce((sum, row) => sum + row.totalFit, 0);
    const protectedFit = failureRows.reduce((sum, row) => sum + row.protectedFit, 0);
    const residualFit = failureRows.reduce((sum, row) => sum + row.residualFit, 0);
    const singlePointFit = failureRows.reduce((sum, row) => sum + row.spfFit, 0);
    const latentFaultFit = failureRows.reduce((sum, row) => sum + row.latentFit, 0);
    const averageCoveragePct = failureRows.length
      ? failureRows.reduce((sum, row) => sum + row.diagnosticCoveragePct, 0) / failureRows.length
      : 0;

    const spfMetricPct = totalFit > 0 ? (1 - singlePointFit / totalFit) * 100 : 100;
    const lfMetricPct = totalFit > 0 ? (1 - latentFaultFit / totalFit) * 100 : 100;

    return {
      componentCount: new Set(rows.map((row) => row.componentDesignator)).size,
      failureCount: failureRows.length,
      totalFit: this.roundMetric(totalFit),
      protectedFit: this.roundMetric(protectedFit),
      residualFit: this.roundMetric(residualFit),
      averageCoveragePct: this.roundMetric(averageCoveragePct),
      singlePointFit: this.roundMetric(singlePointFit),
      latentFaultFit: this.roundMetric(latentFaultFit),
      spfMetricPct: this.roundMetric(spfMetricPct),
      lfMetricPct: this.roundMetric(lfMetricPct)
    };
  });

  getFailureModesForComponent(componentDesignator: string) {
    const components = this.store.components();
    const componentTypes = this.store.componentTypes();
    const selectedComponent = components.find((component) => component.component_designator === componentDesignator);
    if (!selectedComponent) {
      return [];
    }

    return componentTypes.find((componentType) => componentType.name === selectedComponent.component_type)?.failure_modes ?? [];
  }

  getRemainingFailureModesForComponent(componentDesignator: string) {
    const failureModes = this.getFailureModesForComponent(componentDesignator);
    if (failureModes.length === 0) {
      return [];
    }

    const assignedModeCodes = new Set(
      this.store.failureInstances()
        .filter((failureInstance) => failureInstance.component_designator === componentDesignator)
        .map((failureInstance) => failureInstance.failure_mode_code)
    );

    return failureModes.filter((mode) => !assignedModeCodes.has(mode.code));
  }

  getRemainingFailureModeCount(componentDesignator: string): number {
    return this.getRemainingFailureModesForComponent(componentDesignator).length;
  }

  buildFailureInstanceName(componentDesignator: string, failureModeCode: string): string {
    const normalizedComponent = this.normalizeFiToken(componentDesignator);
    const normalizedMode = this.normalizeFiToken(failureModeCode);
    return `${normalizedComponent}_${normalizedMode}_EVENT`;
  }

  private normalizeFiToken(value: string): string {
    return value
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/_+/g, '_');
  }
}

